import { useEffect, useState, type FC } from 'react';
import { InlineLoading, InlineNotification, TextArea } from '@carbon/react';
import { SidePanel } from '@carbon/ibm-products';
import { useAreaUsages } from 'pages/area-geometries/show/use-area-usages';
import {
  usageItemHref,
  usageTypeLabel,
} from 'pages/area-geometries/show/area-usages';
import 'components/inline-edit/ibm-products-config';
import styles from './area-lifecycle-panel.module.scss';

/** The two lifecycle changes that warn before they act. */
export type AreaLifecycleAction = 'promote' | 'archive';

interface AreaLifecyclePanelProps {
  open: boolean;
  action: AreaLifecycleAction;
  /** The area being changed, named throughout the copy. */
  areaName: string;
  /** The workspace the change is made from, for the subtitle. */
  workspaceName: string;
  /** The working copy, for the usages check. */
  areaGeometryId: number;
  /** The stable area id, which the usages check prefers. */
  businessId?: string;
  /** Whether the area is on the map today — promote only names the layer if it is. */
  shared: boolean;
  /** True while the write is in flight. */
  busy: boolean;
  /** Why the last attempt was refused, or null. */
  error: string | null;
  onClose: () => void;
  /** Confirms, with whatever reason was typed (empty string when none). */
  onConfirm: (reason: string) => void;
}

// What each action says. Kept together so the wording is reviewable in one place —
// most of it is Dustin's from the v13 mock.
const COPY: Record<
  AreaLifecycleAction,
  {
    title: string;
    body: (areaName: string) => string;
    /** The consequence for the records pointing at this area. */
    consequence: string;
    /** Said instead, where nothing references the area at all. */
    emptyTitle: string;
    emptyBody: string;
    tone: 'warning' | 'error';
    headline: (count: number) => string;
    reasonLabel: string;
    reasonPlaceholder: string;
    confirmLabel: string;
    confirmKind: 'primary' | 'danger';
  }
> = {
  promote: {
    title: 'Promote Area',
    // The layer clause finishes the same sentence, so it is folded in by the caller
    // rather than trailing as one of its own.
    body: (areaName) =>
      `Promoting persists this workspace's instance of the Area geometry and details as the canonical record for this Area. Downstream consumers will use this instance as the default for the ${areaName}`,
    // Warning-only this release: nothing is re-pointed or recalculated for the
    // consumer, so the copy says to check rather than promising it happens.
    consequence:
      'The items below keep the version they were linked to. Check whether any of them should be re-pointed at the promoted boundary.',
    tone: 'warning',
    headline: (count) =>
      `${count} ${
        count === 1 ? 'item references' : 'items reference'
      } this Area`,
    emptyTitle: 'Nothing downstream relies on this Area today',
    emptyBody:
      'Nothing recalculates now, but this instance becomes the canonical record that every future consumer picks up.',
    reasonLabel: 'Reason for promoting',
    reasonPlaceholder:
      'e.g. Boundary corrected against the 2026 GIS import; acreage now matches the recorded plat.',
    confirmLabel: 'Promote',
    confirmKind: 'primary',
  },
  archive: {
    title: 'Archive Area',
    body: (areaName) =>
      `Archiving takes ${areaName} out of play: it is no longer accessible by default for new work, and drops out of the Areas catalog, the map layer list, and the pickers that offer it.`,
    consequence:
      'Once archived, the Area is not accessible by default. The items below may need to be updated to reference a different Area.',
    tone: 'error',
    headline: (count) =>
      `${count} ${count === 1 ? 'item relies' : 'items rely'} on this Area`,
    emptyTitle: 'Nothing relies on this Area today',
    emptyBody:
      'Archiving it now breaks nothing. It drops out of the catalog, the map layer list and the pickers, and can be restored from a new workspace.',
    reasonLabel: 'Reason for archiving',
    reasonPlaceholder:
      'e.g. Superseded by the 2026 unit re-plat — no longer a valid boundary.',
    confirmLabel: 'Archive',
    confirmKind: 'danger',
  },
};

// The panel that stands between a lifecycle change and the records that depend on the
// area: what the change does, who references it, and why it is being made. Promote,
// archive and remove-from-map differ only in wording and in what the caller then
// writes, so they share this one panel.
export const AreaLifecyclePanel: FC<AreaLifecyclePanelProps> = ({
  open,
  action,
  areaName,
  workspaceName,
  areaGeometryId,
  businessId,
  shared,
  busy,
  error,
  onClose,
  onConfirm,
}) => {
  const [reason, setReason] = useState('');
  const copy = COPY[action];

  // SidePanel pushes #main-content via inline margin and only clears it on open→false.
  // Closing unmounts this panel, and confirming unmounts it mid-open, so clear the push
  // on unmount either way.
  useEffect(
    () => () => {
      const pageContent = document.querySelector('#main-content');
      if (pageContent instanceof HTMLElement) {
        pageContent.style.marginInlineEnd = '';
        pageContent.style.marginInlineStart = '';
        pageContent.style.inlineSize = '';
      }
    },
    []
  );

  // Only asked while a panel is open, and re-asked each time one opens: an area's
  // references change under it as other people work.
  const usages = useAreaUsages(areaGeometryId, open, businessId);

  // A reason belongs to the attempt that was made, not to the next one.
  useEffect(() => {
    if (open) {
      setReason('');
    }
  }, [open, action]);

  return (
    <SidePanel
      open={open}
      onRequestClose={onClose}
      title={copy.title}
      subtitle={`Area Workspace · ${workspaceName}`}
      slideIn
      selectorPageContent="#main-content"
      placement="right"
      className={styles.panel}
      size="md"
      animateTitle={false}
      actions={[
        { kind: 'secondary' as const, label: 'Cancel', onClick: onClose },
        {
          kind: copy.confirmKind,
          label: busy ? `${copy.confirmLabel}…` : copy.confirmLabel,
          onClick: () => onConfirm(reason.trim()),
          // Held while the references are being counted: this panel exists to warn
          // first, and acting before the warning arrives defeats it. A failed check
          // does not hold it — that says nothing about the area either way.
          disabled: busy || usages.loading,
        },
      ]}
    >
      <div className={styles.body}>
        <p className={styles.description}>
          {copy.body(areaName)}
          {/* Only promote talks about a layer, and only when there is one. */}
          {action === 'promote'
            ? shared
              ? ', and the Area is published as a layer on every Insights map.'
              : '.'
            : ''}
        </p>

        {error && (
          <InlineNotification
            kind="error"
            lowContrast
            hideCloseButton
            title={`Could not ${copy.confirmLabel.toLowerCase()} the area`}
            subtitle={error}
          />
        )}

        {usages.error && (
          <InlineNotification
            kind="warning"
            lowContrast
            hideCloseButton
            title="Could not check references"
            subtitle={`${usages.error} Continue only if you know what points at this area.`}
          />
        )}

        {/* A spinner rather than a line of prose: the check is usually quick, and a
            plain sentence appearing and vanishing reads as a glitch. */}
        {usages.loading && (
          <InlineLoading
            status="active"
            description="Checking what references this Area…"
          />
        )}

        {!usages.loading && !usages.error && usages.total === 0 && (
          <InlineNotification
            kind="warning"
            lowContrast
            hideCloseButton
            title={copy.emptyTitle}
            subtitle={copy.emptyBody}
          />
        )}

        {usages.total > 0 && (
          <>
            <InlineNotification
              kind={copy.tone}
              lowContrast
              hideCloseButton
              title={copy.headline(usages.total)}
              subtitle={copy.consequence}
            />

            {usages.groups.map((group) => (
              <section className={styles.group} key={group.type}>
                <h3 className={styles.groupTitle}>
                  {usageTypeLabel(group.type)} ({group.count})
                </h3>
                <ul className={styles.items}>
                  {(group.items ?? []).map((item) => {
                    const href = usageItemHref(group.type, item.id);
                    return (
                      <li
                        className={styles.item}
                        key={`${group.type}-${item.id}`}
                      >
                        {href ? (
                          <a href={href} target="_blank" rel="noreferrer">
                            {item.label}
                          </a>
                        ) : (
                          item.label
                        )}
                      </li>
                    );
                  })}
                </ul>
              </section>
            ))}
          </>
        )}

        <TextArea
          id={`area-lifecycle-reason-${action}`}
          labelText={copy.reasonLabel}
          placeholder={copy.reasonPlaceholder}
          rows={3}
          value={reason}
          onChange={(event) => setReason(event.target.value)}
        />
      </div>
    </SidePanel>
  );
};
